/**
 * Captura conversacional: contrato común para cualquier intérprete de órdenes.
 *
 * Hoy solo existe el intérprete determinista (deterministic.ts), sin coste ni red.
 * Un proveedor de IA futuro implementaría CommandInterpreter y su salida se validaría
 * con interpretationSchema antes de mostrarse: nunca se aplica nada sin que el
 * usuario confirme la propuesta.
 */
import { z } from 'zod';

const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const name = z.string().trim().min(1).max(80);

export const scheduleSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('daily') }),
  z.object({ type: z.literal('weekdays'), days: z.array(z.number().int().min(1).max(7)).min(1).max(7) }),
  z.object({ type: z.literal('weekly_quota'), mode: z.literal('times'), times: z.number().int().min(1).max(7) }),
]);

export const proposalSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('create_habit'),
    name,
    habitKind: z.enum(['build', 'quit']),
    schedule: scheduleSchema,
    goalType: z.enum(['binary', 'numeric']),
    target: z.number().min(0).max(100_000),
    unit: z.string().max(24).nullable(),
    /** Recordatorio opcional (planificación): hora y días; null = todos los días programados. */
    reminder: z.object({ time: hhmm, days: z.array(z.number().int().min(1).max(7)).min(1).max(7).nullable() }).nullable().optional(),
  }),
  z.object({ kind: z.literal('create_task'), name, dueDate: localDate.nullable(), dueTime: hhmm.nullable(), reminder: z.boolean() }),
  z.object({ kind: z.literal('add_to_list'), listName: name, listId: z.string().nullable(), items: z.array(z.string().trim().min(1).max(200)).min(1).max(50) }),
  z.object({ kind: z.literal('show_today') }),
  z.object({
    kind: z.literal('log_activity'),
    habitId: z.string(),
    habitName: name,
    date: localDate,
    /** null = marcar como hecho (hábitos binarios). */
    amount: z.number().positive().max(100_000).nullable(),
    unit: z.string().max(24).nullable(),
  }),
  z.object({
    kind: z.literal('create_note'),
    noteKind: z.enum(['note', 'idea', 'fact', 'quote', 'contact']),
    title: z.string().trim().min(1).max(120),
    /** Autor de una cita o datos de un contacto. */
    body: z.string().trim().max(500).nullable().optional(),
  }),
  z.object({ kind: z.literal('journal'), text: z.string().trim().min(1).max(20_000) }),
  z.object({ kind: z.literal('start_focus'), minutes: z.number().int().min(1).max(180), habitId: z.string().nullable(), label: z.string().max(80).nullable() }),
  /** Editar un hábito existente desde hoy (el historial no cambia). Solo los campos presentes. */
  z.object({
    kind: z.literal('update_habit'),
    habitId: z.string(),
    habitName: name,
    name: name.optional(),
    goalType: z.enum(['binary', 'numeric']).optional(),
    target: z.number().min(0).max(100_000).optional(),
    unit: z.string().max(24).nullable().optional(),
    schedule: scheduleSchema.optional(),
  }),
  /** Crear o cambiar el recordatorio de un hábito o tarea. */
  z.object({ kind: z.literal('set_reminder'), habitId: z.string(), habitName: name, time: hhmm, days: z.array(z.number().int().min(1).max(7)).min(1).max(7).nullable() }),
  z.object({ kind: z.literal('reschedule_task'), habitId: z.string(), habitName: name, dueDate: localDate.nullable(), dueTime: hhmm.nullable() }),
  /** Registrar una sesión de enfoque ya hecha (no inicia el temporizador). */
  z.object({ kind: z.literal('log_focus'), minutes: z.number().int().min(1).max(180), habitId: z.string().nullable(), label: z.string().max(80).nullable(), date: localDate }),
  z.object({ kind: z.literal('archive_habit'), habitId: z.string(), habitName: name }),
  z.object({ kind: z.literal('delete_habit'), habitId: z.string(), habitName: name }),
]);

export type Proposal = z.infer<typeof proposalSchema>;
export type ProposalKind = Proposal['kind'];

/** Acciones que borran u ocultan datos: requieren una confirmación explícita. */
export const DESTRUCTIVE: ReadonlySet<ProposalKind> = new Set(['archive_habit', 'delete_habit']);

export const interpretationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('proposal'), proposal: proposalSchema }),
  z.object({
    type: z.literal('clarify'),
    /** Clave de texto de la pregunta (i18n) y valores para rellenarla. */
    question: z.string(),
    params: z.record(z.union([z.string(), z.number()])).default({}),
    options: z.array(z.object({ label: z.string(), proposal: proposalSchema })).max(6),
  }),
  z.object({ type: z.literal('unsupported') }),
]);

export type Interpretation = z.infer<typeof interpretationSchema>;

export interface InterpreterContext {
  today: string;
  /** Instante actual (ms) y zona horaria efectiva: para "hoy a las 18:00" ya pasado. */
  now: number;
  tz: string;
  locale: 'es' | 'en';
  habits: { id: string; name: string; kind: 'build' | 'quit' | 'task'; goalType: 'binary' | 'numeric'; unit: string | null; defaultIncrement: number; archived: boolean; completed: boolean }[];
  lists: { id: string; title: string }[];
}

export interface CommandInterpreter {
  readonly id: string;
  interpret(text: string, ctx: InterpreterContext): Promise<Interpretation>;
}

/** Valida la salida de cualquier intérprete; lo inválido se trata como no admitido. */
export function validateInterpretation(raw: unknown): Interpretation {
  const r = interpretationSchema.safeParse(raw);
  return r.success ? r.data : { type: 'unsupported' };
}
